// Header.tsx
import React, { Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  ThemeProvider,
  Button,
  Fade,
  Menu,
  MenuItem,
} from "@mui/material";
import { theme } from "../Color/color";
import logo from "../../images/rakuCADtrace_logo.png";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LoginIcon from "@mui/icons-material/Login";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import Cookies from "js-cookie";
import { clearAccount } from "../../features/accountInfo/accountInfoSlice";
import { reset } from "../../features/customerInfo/customerInfoSlice";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
interface HeaderProps {
  pageTitle?: string;
  hideTop?: Boolean;
  disableLogout?: Boolean;
}

const Header: React.FC<HeaderProps> = ({
  pageTitle,
  hideTop = false,
  disableLogout = false,
}) => {
  // ナビゲーション
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const accountInfo = useAppSelector((state) => state.accountInfo);
  const dispatch = useAppDispatch();
  const token = Cookies.get("access_token");

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigateManagement = (path: string) => {
    navigate(`/management/${path}`);
  };
  const handleNavigateDashboard = () => {
    navigate(`/dashboard/${accountInfo.type}`);
  };

  const handleLogout = async () => {
    navigate("/login");
    if (!disableLogout) {
      dispatch(reset());
      dispatch(clearAccount());
      Cookies.remove("access_token");
    }
  };

  // TOP画面でのみキャンペーン帯を表示する（顧客アカウントでのログイン時に加え、未ログイン時も表示）
  const showCampaignBanner =
    location.pathname === "/" &&
    (accountInfo.type === "customer" || accountInfo.type === "");

  return (
    <AppBar position="static" sx={{ zIndex: 1300 }}>
      <Toolbar
        sx={{ backgroundColor: "#ffffff", borderBo: "1px solid #CCCCCC" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ flex: "0 1 16%" }}>
            <img
              src={logo}
              alt="raku cad trace"
              style={{
                height: "100%",
                maxHeight: 60,
                aspectRatio: "4.25:1",
                marginLeft: 0,
                marginBottom: 4,
              }}
            />
          </Box>
          <Box sx={{ flex: "0 1 44%" }}>
            {pageTitle && (
              <Typography
                variant="h5"
                color="black"
                sx={{ paddingLeft: "20px" }}
              >
                {pageTitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: "0 1 40%", textAlign: "right" }}>
            {!hideTop && (
              <ThemeProvider theme={theme}>
                {(accountInfo.type === "customer" ||
                  accountInfo.type === "") && (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate("/");
                    }}
                  >
                    TOPへ戻る
                  </Button>
                )}
                {token ? (
                  <Button
                    id="fade-button"
                    aria-controls={open ? "fade-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    style={{ marginLeft: "15px" }}
                  >
                    <AccountCircleIcon className="mr-4" />
                    {accountInfo.username ?? "ユーザー名"}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      style={{ marginLeft: "15px" }}
                      component={Link}
                      to="/register"
                    >
                      <PersonAddAltIcon className="mr-1" />
                      新規登録
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      style={{ marginLeft: "15px" }}
                      component={Link}
                      to="/login"
                    >
                      <LoginIcon className="mr-1" />
                      ログイン
                    </Button>
                  </>
                )}
              </ThemeProvider>
            )}
          </Box>
          <Menu
            id="fade-menu"
            MenuListProps={{
              "aria-labelledby": "fade-button",
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
          >
            <div style={{ color: "#3E6EB4" }}>
              <MenuItem onClick={() => handleNavigateDashboard()}>
                ダッシュボード
              </MenuItem>
              {accountInfo.roles.admin && (
                <Fragment>
                  <MenuItem
                    onClick={() => handleNavigateManagement("authority")}
                  >
                    権限管理
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigateManagement("customer")}
                  >
                    顧客アカウント管理
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigateManagement("subcontractor")}
                  >
                    外注先アカウント管理
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleNavigateManagement("employee")}
                  >
                    自社アカウント管理
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigateManagement("system")}>
                    システム設定
                  </MenuItem>
                </Fragment>
              )}
              <MenuItem onClick={() => handleLogout()}>ログアウト</MenuItem>
            </div>
          </Menu>
        </Box>
      </Toolbar>
      {showCampaignBanner && (
        <Box
          sx={{
            backgroundColor: "#FFF4E5",
            borderTop: "1px solid #F0C36D",
            borderBottom: "1px solid #F0C36D",
            color: "#7A4F01",
            px: 3,
            py: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
            【10％割引キャンペーン実施中】
          </Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>
            ※本システムにてお見積もり・ご発注いただいたお客様が対象となります。
          </Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>
            ※本システムにてお見積もりできなかった画像は、キャンペーンの対象外となります。
          </Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>
            ※システム上のお見積書には、割引前の正規金額が表示されますので、ご注意ください。
          </Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>
            ※受注確認後、弊社より10％割引についてメールでご案内し、10％割引後の金額にてご請求いたします。
          </Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>
            ※本キャンペーンは予告なく終了する場合がございます。
          </Typography>
        </Box>
      )}
    </AppBar>
  );
};

export default Header;
